import React, { useState, useEffect, useCallback, useContext } from 'react';
import apiClient from '../api';
import AuthContext from '../context/AuthContext';
import FamilyMemberForm from './FamilyMemberForm';

function FamilyMembers({ juvenile }) {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const { hasPermission } = useContext(AuthContext);

  const fetchMembers = useCallback(async () => {
    if (!juvenile?.id) return;
    try {
      setIsLoading(true);
      const response = await apiClient.get(`/api/family/juveniles/${juvenile.id}`);
      setMembers(response.data);
    } catch (err) {
      setError('Failed to fetch family members.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [juvenile?.id]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleFormSubmit = async (formData) => {
    try {
      if (editingMember) {
        await apiClient.put(`/api/family/${editingMember.id}`, formData);
      } else {
        await apiClient.post(`/api/family/juveniles/${juvenile.id}`, formData);
      }
      setIsModalOpen(false);
      setEditingMember(null);
      fetchMembers();
    } catch (err) {
      console.error('Failed to save family member:', err);
      alert(err.response?.data?.msg || 'Failed to save family member.');
    }
  };

  const handleDelete = async (memberId) => {
    if (window.confirm('Are you sure you want to delete this family member?')) {
      try {
        await apiClient.delete(`/api/family/${memberId}`);
        fetchMembers();
      } catch (err) {
        console.error('Failed to delete family member:', err);
        alert('Failed to delete family member.');
      }
    }
  };

  if (isLoading) return <div className="card"><p>Loading family members...</p></div>;
  if (error) return <div className="card"><p className="text-red-500">{error}</p></div>;

  return (
    <div className="card space-y-8">
      <div>
        <h2 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Juvenile Contact Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-slate-600">
          <p><strong>Phone:</strong> {juvenile.contact_info?.phone || 'N/A'}</p>
          <p><strong>Address:</strong> {juvenile.contact_info?.address || 'N/A'}</p>
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-slate-800 mb-0">Family & Guardians</h2>
          {hasPermission('manage-family-members') && (
            <button onClick={() => setIsModalOpen(true)} className="bg-gold text-white hover:bg-gold-dark font-bold py-2 px-4 rounded-lg shadow-sm">
              + Add Family Member
            </button>
          )}
        </div>
        <div className="space-y-4">
          {members.length > 0 ? (
            members.map(member => (
              // --- THIS IS THE DISPLAY LOGIC THAT WAS MISSING ---
              <div key={member.id} className="p-4 border rounded-lg bg-slate-50">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-slate-700">{member.full_name} <span className="text-sm font-normal text-slate-500">- {member.relationship}</span></h3>
                    {member.is_primary_guardian && <p className="text-xs font-bold text-green-700 bg-green-100 inline-block px-2 py-0.5 rounded-full">PRIMARY GUARDIAN</p>}
                  </div>
                  {hasPermission('manage-family-members') && (
                    <div className="flex gap-2">
                      <button onClick={() => { setEditingMember(member); setIsModalOpen(true); }} className="text-sm text-slate-600 hover:text-gold">Edit</button>
                      <button onClick={() => handleDelete(member.id)} className="text-sm text-red-600 hover:text-red-800">Delete</button>
                    </div>
                  )}
                </div>
                <div className="border-t mt-2 pt-2 text-sm text-slate-600 space-y-1">
                  <p><strong>Phone:</strong> {member.contact_info?.phone || 'N/A'}</p>
                  <p><strong>Address:</strong> {member.contact_info?.address || 'N/A'}</p>
                  {member.notes && <p><strong>Notes:</strong> {member.notes}</p>}
                </div>
              </div>
            ))
          ) : (
            <p>No family members have been added for this juvenile.</p>
          )}
        </div>
      </div>

      {isModalOpen && (
        <FamilyMemberForm
          initialData={editingMember || {}}
          onSubmit={handleFormSubmit}
          onCancel={() => { setIsModalOpen(false); setEditingMember(null); }}
        />
      )}
    </div>
  );
}

export default FamilyMembers;